import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, PoundSterling, ChevronRight, Wallet, Compass, Award, BadgeCheck, Users } from "lucide-react";
import InstructorBottomNav from "../components/instructor/InstructorBottomNav";
import RevenueDashboard from "./RevenueDashboard";
import { InstructorStats } from "../components/instructor/InstructorStats";
import BriefImpactPanel from "../components/instructor/BriefImpactPanel";
import RecurringIncomePanel from "../components/instructor/RecurringIncomePanel";
import UpgradePrompt from "../components/instructor/UpgradePrompt";
import PlatformFeeModePanel from "../components/instructor/PlatformFeeModePanel";
import PasskeyEnrollPanel from "../components/auth/PasskeyEnrollPanel";
import CardErrorBoundary from "../components/CardErrorBoundary";
import BankConnectionPanel from "../components/BankConnectionPanel";
import ServiceControls from "../components/ServiceControls";
import { useInstructorTier } from "../hooks/useInstructorTier";

/**
 * InstructorMoney — Phase 11.2 (Feb 6, 2026): the "Money" tab.
 *
 * Financial control centre only. Revenue, wallet, expenses, mileage,
 * cash-out, accountant reports, subscription, billing.
 *
 * Per locked map: NO performance metrics (those go to Progress),
 * NO learning content, NO operational tools.
 */
export default function InstructorMoney() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { has } = useInstructorTier();
  const role = (user?.role || "adi").toLowerCase();
  const isPdi = role === "pdi";

  return (
    <div data-testid="instructor-money-page" className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-30">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center gap-3">
          <button data-testid="money-back-btn" onClick={() => nav(isPdi ? "/pdi-hub" : "/instructor")} className="text-slate-400 hover:text-slate-700 dark:hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <PoundSterling className="w-5 h-5 text-emerald-600" />
          <p className="font-extrabold text-slate-900 dark:text-white text-base">Money</p>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 pt-4 space-y-4">
        {/* B-money (Feb 27, 2026 exposure batch) — earnings channel
            strip. Quiet horizontal navigation to the 5 real income
            channels the ADI/PDI already has access to. Pure exposure:
            zero new endpoints, no fabricated numbers, no fake badges.
            Each tile links to a route that exists today. PDI keeps
            the same channels — they're allowed to teach group
            sessions and run TrainerPath / Mastery / Licences. See
            /app/memory/OPERATIONAL_EXPOSURE_MAPPING_V1.md §3.B3. */}
        <section data-testid="money-earnings-channels" className="space-y-2">
          <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500">
            Earnings channels
          </h2>
          <div className="grid grid-cols-2 gap-2">
            <ChannelTile
              testid="channel-tile-freedom-wallet"
              label="Freedom wallet"
              desc="Cash-out · expenses · mileage"
              Icon={Wallet}
              tone="emerald"
              onClick={() => nav("/instructor/freedom")}
            />
            <ChannelTile
              testid="channel-tile-trainer-path"
              label="TrainerPath"
              desc="Become a PDI trainer"
              Icon={Compass}
              tone="indigo"
              onClick={() => nav("/trainer-path")}
            />
            <ChannelTile
              testid="channel-tile-mastery"
              label="Mastery Builder"
              desc="Sell focused skill modules"
              Icon={Award}
              tone="violet"
              onClick={() => nav("/instructor/mastery")}
            />
            <ChannelTile
              testid="channel-tile-licences"
              label="Licences"
              desc="Passport licence packs"
              Icon={BadgeCheck}
              tone="amber"
              onClick={() => nav("/instructor/licences")}
            />
            <ChannelTile
              testid="channel-tile-group-sessions"
              label="Group sessions"
              desc="Multi-pupil revenue"
              Icon={Users}
              tone="sky"
              onClick={() => nav("/group-sessions")}
            />
          </div>
        </section>

        {/* Basic money view — visible to all tiers (Today / Week stats,
            simple paid/unpaid totals). RevenueDashboard renders its
            "understand money" half for everyone; advanced
            forecasting/pattern panels gate to Growth via `showAdvanced`.

            Phase 1.3 alive-contract migration (May 19, 2026): the
            income summary surface is wrapped in CardErrorBoundary so
            a render crash inside InstructorStats can never blank the
            Money tab. InstructorStats itself uses <AsyncSurface> for
            its self-fetch path — loading / error / empty all surface
            calmly rather than silently rendering £0/£0/0. */}
        <CardErrorBoundary region="income-summary" label="Income summary">
          <InstructorStats variant="revenue" testidPrefix="money-stats" />
        </CardErrorBoundary>
        {!isPdi && <RevenueDashboard showAdvanced={has("growth")} />}

        <section data-testid="money-financial-section" className="space-y-2">
          <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500">Financial centre</h2>
          <Tile
            testid="money-tile-payment-confirmations"
            label="Payment confirmations"
            desc="Receipts · Stripe refs · manual paid · pending"
            onClick={() => nav("/instructor/money/confirmations")}
          />
          <Tile
            testid="money-tile-freedom-dashboard"
            label="Freedom Dashboard"
            desc="Wallet · expenses · mileage · cash-out"
            onClick={() => nav("/instructor/freedom")}
          />
          <BankConnectionPanel />
          <ServiceControls />
          <BriefImpactPanel />

          {/* Recurring Income + advanced revenue analytics — Growth-tier
              only. Feb 6, 2026: non-Growth users see ONE clean inline
              upgrade card here, per owner directive ("Maintain a stable
              interface while progressively unlocking capability"). */}
          {has("growth") ? (
            <RecurringIncomePanel />
          ) : (
            <UpgradePrompt
              testId="money-recurring-upgrade"
              title="Money in"
              upgradeTier="Growth"
              bullets={[
                "Recurring Income — £2/month per pupil on Membership",
                "Advanced revenue analytics + 8-week forecasting",
                "Income optimisation tools",
              ]}
            />
          )}
        </section>

        <section data-testid="money-billing-section" className="space-y-2">
          <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500">Subscription & billing</h2>
          <Tile testid="money-tile-pricing"      label="Plans & subscription"  desc="Manage your plan"      onClick={() => nav("/pricing")} />
          <Tile testid="money-tile-sms-settings" label="SMS notifications"     desc="Twilio templates · per-message billing" onClick={() => nav("/settings/sms")} />
          {/* R1.2 — instructor-level toggle for who covers the platform fee.
              Hidden when the platform-fee flag is off (prod safety). */}
          <PlatformFeeModePanel />
          {/* P0 (Feb 7 2026) — biometric soft-refresh. Hidden when the
              device doesn't support a platform authenticator. */}
          <PasskeyEnrollPanel />
        </section>

        {isPdi && (
          <section data-testid="money-pdi-section" className="space-y-2">
            <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500">PDI fees</h2>
            <p className="text-[11px] text-slate-500 px-1">Earnings (if applicable) and fees paid to your ADI / school.</p>
            <Tile testid="money-tile-pdi-freedom" label="My earnings & fees" desc="Open detailed view" onClick={() => nav("/instructor/freedom")} />
          </section>
        )}
      </main>

      <InstructorBottomNav role={role} active="money" />
    </div>
  );
}

function Tile({ testid, label, desc, onClick }) {
  return (
    <button data-testid={testid} onClick={onClick} className="w-full text-left rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3 flex items-center gap-3 hover:border-emerald-300 active:scale-[0.99] transition-all">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-slate-900 dark:text-white">{label}</p>
        <p className="text-[11px] text-slate-500 mt-0.5">{desc}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
    </button>
  );
}

const CHANNEL_TONES = {
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200" },
  indigo:  { bg: "bg-indigo-50",  text: "text-indigo-700",  ring: "ring-indigo-200" },
  violet:  { bg: "bg-violet-50",  text: "text-violet-700",  ring: "ring-violet-200" },
  amber:   { bg: "bg-amber-50",   text: "text-amber-700",   ring: "ring-amber-200" },
  sky:     { bg: "bg-sky-50",     text: "text-sky-700",     ring: "ring-sky-200" },
};

function ChannelTile({ testid, label, desc, Icon, tone = "emerald", onClick }) {
  const t = CHANNEL_TONES[tone] || CHANNEL_TONES.emerald;
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-left rounded-2xl bg-white dark:bg-slate-900 ring-1 ${t.ring} p-3 hover:shadow-sm active:scale-[0.99] transition-all`}
    >
      <div className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${t.bg} ${t.text} mb-2`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-sm font-bold text-slate-900 dark:text-white leading-tight">
        {label}
      </p>
      <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{desc}</p>
    </button>
  );
}
